import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import workspaceApi from '../../utils/workspaceApi';
import WorkspaceSearch from './WorkspaceSearch';
import WorkspaceGrid from './WorkspaceGrid';
import WorkspaceContextMenu from './WorkspaceContextMenu';
import './WorkspaceLauncher.css';

/**
 * Preferred display order. Categories the backend sends that are missing from
 * this list are appended at the end rather than dropped, so adding a module to
 * the catalog can never make it silently vanish from the launcher.
 */
const CATEGORY_ORDER = [
  'Communication',
  'Content',
  'Productivity',
  'AI',
  'Storage',
  'Analytics',
  'Organization',
  'Administration',
];

const RECENT_LIMIT = 8;

/** Client-side mirror of the backend ordering: favorites first, then appOrder. */
const sortApps = (apps, appOrder = [], favorites = []) => {
  const orderIndex = new Map(appOrder.map((id, i) => [id, i]));
  const favIndex = new Map(favorites.map((id, i) => [id, i]));
  return [...apps].sort((a, b) => {
    const aFav = favIndex.has(a.id);
    const bFav = favIndex.has(b.id);
    if (aFav !== bFav) return aFav ? -1 : 1;
    if (aFav && bFav) return favIndex.get(a.id) - favIndex.get(b.id);
    const ao = orderIndex.has(a.id) ? orderIndex.get(a.id) : Number.MAX_SAFE_INTEGER;
    const bo = orderIndex.has(b.id) ? orderIndex.get(b.id) : Number.MAX_SAFE_INTEGER;
    return ao - bo;
  });
};

const matchesQuery = (app, q) => {
  if (!q) return true;
  const haystack = [app.name, app.category, app.description, ...(app.keywords || [])]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
};

/**
 * WorkspaceLauncher
 * The floating, glassmorphism ATOZAS Workspace panel. Owns all launcher state:
 * apps, preferences, summary, suggestions, search, drag-reorder and the
 * right-click menu. Closes on ESC, outside click, or a re-click of the anchor
 * button (handled by the parent toggling `open`).
 */
const WorkspaceLauncher = ({ open, onClose, currentUser, anchorRef }) => {
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const loadedRef = useRef(false);

  const [apps, setApps] = useState([]);
  const [prefs, setPrefs] = useState({
    favorites: [],
    recentApps: [],
    hiddenApps: [],
    appOrder: [],
    gridSize: 'large',
    theme: 'system',
  });
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeAppId, setActiveAppId] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);

  const appsById = useMemo(() => new Map(apps.map((a) => [a.id, a])), [apps]);

  // --------------------------------------------------------------- Data loading
  const loadData = useCallback(async () => {
    if (!loadedRef.current) setLoading(true);
    try {
      const appsRes = await workspaceApi.getApps();
      if (appsRes?.success) {
        setApps(appsRes.apps || []);
        setPrefs((prev) => ({ ...prev, ...(appsRes.prefs || {}) }));
      }
      loadedRef.current = true;
    } catch (err) {
      // Silent: keep any previously loaded state.
      console.error('Workspace load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadData();
      setQuery('');
      setContextMenu(null);
      // Focus search shortly after the open animation begins.
      const t = setTimeout(() => searchRef.current?.focus(), 90);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, loadData]);

  // --------------------------------------------------------------- Close handlers
  useEffect(() => {
    if (!open) return undefined;

    const handleKey = (e) => {
      if (e.key === 'Escape') {
        if (contextMenu) setContextMenu(null);
        else onClose();
      }
    };
    const handleDown = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return; // re-click toggles via parent
      if (e.target.closest?.('.ws-context-menu')) return;
      onClose();
    };

    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleDown);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleDown);
    };
  }, [open, onClose, anchorRef, contextMenu]);

  // --------------------------------------------------------------- Open an app
  const openApp = useCallback(
    (appOrId) => {
      const app = typeof appOrId === 'string' ? appsById.get(appOrId) : appOrId;
      if (!app) return;

      setActiveAppId(app.id);
      // Persist + optimistically update recents.
      workspaceApi.open(app.id).catch(() => {});
      setPrefs((prev) => ({
        ...prev,
        recentApps: [app.id, ...(prev.recentApps || []).filter((id) => id !== app.id)].slice(0, RECENT_LIMIT),
      }));

      if (app.action) {
        window.dispatchEvent(new CustomEvent(app.action, { detail: { app } }));
      }
      if (app.route) {
        navigate(app.route);
      }
      onClose();
    },
    [appsById, navigate, onClose]
  );

  const openAppNewTab = useCallback(
    (app) => {
      if (app.route) {
        window.open(app.route, '_blank', 'noopener');
        workspaceApi.open(app.id).catch(() => {});
      } else {
        openApp(app);
      }
    },
    [openApp]
  );

  // --------------------------------------------------------------- Favorites
  const toggleFavorite = useCallback(
    (app) => {
      const nextFav = !app.isFavorite;
      setApps((prev) => {
        const updated = prev.map((a) => (a.id === app.id ? { ...a, isFavorite: nextFav } : a));
        const favorites = nextFav
          ? [...(prefs.favorites || []).filter((id) => id !== app.id), app.id]
          : (prefs.favorites || []).filter((id) => id !== app.id);
        return sortApps(updated, prefs.appOrder, favorites);
      });
      setPrefs((prev) => ({
        ...prev,
        favorites: nextFav
          ? [...(prev.favorites || []).filter((id) => id !== app.id), app.id]
          : (prev.favorites || []).filter((id) => id !== app.id),
      }));
      workspaceApi.favorite(app.id, nextFav).catch(() => {});
    },
    [prefs.favorites, prefs.appOrder]
  );

  // --------------------------------------------------------------- Hide / show
  const toggleHidden = useCallback((app) => {
    const nextHidden = !app.isHidden;
    setApps((prev) => prev.map((a) => (a.id === app.id ? { ...a, isHidden: nextHidden } : a)));
    setPrefs((prev) => ({
      ...prev,
      hiddenApps: nextHidden
        ? [...(prev.hiddenApps || []).filter((id) => id !== app.id), app.id]
        : (prev.hiddenApps || []).filter((id) => id !== app.id),
    }));
    workspaceApi.hide(app.id, nextHidden).catch(() => {});
  }, []);

  // --------------------------------------------------------------- Move to top
  const moveToTop = useCallback(
    (app) => {
      setApps((prev) => {
        const rest = prev.filter((a) => a.id !== app.id);
        const target = prev.find((a) => a.id === app.id);
        const reordered = target ? [target, ...rest] : prev;
        const appOrder = reordered.map((a) => a.id);
        setPrefs((p) => ({ ...p, appOrder }));
        workspaceApi.reorder(appOrder).catch(() => {});
        return reordered;
      });
    },
    []
  );

  // --------------------------------------------------------------- Drag reorder
  const handleDragStart = useCallback((e, app) => {
    setDragId(app.id);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', app.id);
    } catch (_) {
      /* some browsers require this in a try */
    }
  }, []);

  const handleDragEnter = useCallback(
    (e, app) => {
      if (dragId && dragId !== app.id) setDropTargetId(app.id);
    },
    [dragId]
  );

  const handleDrop = useCallback(
    (e, targetApp) => {
      e.preventDefault();
      const sourceId = dragId;
      setDragId(null);
      setDropTargetId(null);
      if (!sourceId || sourceId === targetApp.id) return;

      const source = appsById.get(sourceId);
      // Only reorder within the same category to keep grouping coherent.
      if (!source || source.category !== targetApp.category) return;

      setApps((prev) => {
        const working = [...prev];
        const from = working.findIndex((a) => a.id === sourceId);
        const to = working.findIndex((a) => a.id === targetApp.id);
        if (from === -1 || to === -1) return prev;
        const [moved] = working.splice(from, 1);
        const insertAt = working.findIndex((a) => a.id === targetApp.id);
        working.splice(insertAt, 0, moved);
        const appOrder = working.map((a) => a.id);
        setPrefs((p) => ({ ...p, appOrder }));
        workspaceApi.reorder(appOrder).catch(() => {});
        return working;
      });
    },
    [dragId, appsById]
  );

  const handleDragEnd = useCallback(() => {
    setDragId(null);
    setDropTargetId(null);
  }, []);

  const dragProps = {
    dragId,
    dropTargetId,
    onDragStart: handleDragStart,
    onDragEnter: handleDragEnter,
    onDragEnd: handleDragEnd,
    onDrop: handleDrop,
  };

  // --------------------------------------------------------------- Grid size
  const setGridSize = useCallback((size) => {
    setPrefs((prev) => ({ ...prev, gridSize: size }));
    workspaceApi.updatePreferences({ gridSize: size }).catch(() => {});
  }, []);

  // --------------------------------------------------------------- Context menu
  const handleContextMenu = useCallback((e, app) => {
    e.preventDefault();
    setContextMenu({ app, x: e.clientX, y: e.clientY });
  }, []);

  // --------------------------------------------------------------- Derived views
  const visibleApps = useMemo(() => apps.filter((a) => !a.isHidden), [apps]);
  const hiddenApps = useMemo(() => apps.filter((a) => a.isHidden), [apps]);
  const gridSize = prefs.gridSize || 'large';
  const trimmedQuery = query.trim().toLowerCase();
  const isSearching = trimmedQuery.length > 0;

  const filteredApps = useMemo(
    () => visibleApps.filter((a) => matchesQuery(a, trimmedQuery)),
    [visibleApps, trimmedQuery]
  );

  const categorized = useMemo(() => {
    const groups = new Map();
    visibleApps.forEach((app) => {
      if (!groups.has(app.category)) groups.set(app.category, []);
      groups.get(app.category).push(app);
    });
    const known = CATEGORY_ORDER.filter((c) => groups.has(c));
    const unknown = [...groups.keys()].filter((c) => !CATEGORY_ORDER.includes(c));
    return [...known, ...unknown].map((c) => ({
      category: c,
      apps: groups.get(c),
    }));
  }, [visibleApps]);

  if (!open) return null;

  const cardHandlers = {
    onOpen: openApp,
    onToggleFavorite: toggleFavorite,
    onContextMenu: handleContextMenu,
  };

  return createPortal(
    <>
      <div className="ws-panel-backdrop" aria-hidden="true" />
      <div
        className="ws-panel"
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="ATOZAS Workspace launcher"
      >
        {/* Header */}
        <div className="ws-header">
          <div className="ws-header-title-row">
            <span className="ws-brand-badge" aria-hidden="true">◆</span>
            <div>
              <h2>ATOZAS Workspace</h2>
              <p className="ws-header-subtitle">Everything you need in one place</p>
            </div>
          </div>
          <button type="button" className="ws-header-close" onClick={onClose} aria-label="Close launcher">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <WorkspaceSearch value={query} onChange={setQuery} inputRef={searchRef} />

        {/* Body */}
        <div className="ws-body">
          {loading ? (
            <div className="ws-loading">
              <div className="ws-spinner" />
              <span>Loading your workspace…</span>
            </div>
          ) : isSearching ? (
            filteredApps.length > 0 ? (
              <div className="ws-section">
                <h3 className="ws-section-title">
                  <span>Results</span>
                  <span className="ws-count-pill">{filteredApps.length}</span>
                </h3>
                <WorkspaceGrid apps={filteredApps} gridSize={gridSize} activeAppId={activeAppId} {...cardHandlers} />
              </div>
            ) : (
              <div className="ws-empty">
                <div className="ws-empty-emoji">🔍</div>
                <p>No apps match “{query}”</p>
              </div>
            )
          ) : (
            <>
              {categorized.map(({ category, apps: catApps }) => (
                <div className="ws-section" key={category}>
                  <h3 className="ws-section-title">
                    <span>{category}</span>
                  </h3>
                  <WorkspaceGrid
                    apps={catApps}
                    gridSize={gridSize}
                    activeAppId={activeAppId}
                    draggable
                    {...dragProps}
                    {...cardHandlers}
                  />
                </div>
              ))}

              {hiddenApps.length > 0 && (
                <div className="ws-section">
                  <button
                    type="button"
                    className="ws-section-title"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    onClick={() => setShowHidden((v) => !v)}
                  >
                    <span>{showHidden ? '▾' : '▸'} Hidden apps</span>
                    <span className="ws-count-pill">{hiddenApps.length}</span>
                  </button>
                  {showHidden && (
                    <WorkspaceGrid apps={hiddenApps} gridSize={gridSize} activeAppId={activeAppId} {...cardHandlers} />
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="ws-footer">
          <span className="ws-footer-hint">
            <kbd>Ctrl</kbd> <kbd>Shift</kbd> <kbd>W</kbd>
          </span>
          <div className="ws-gridsize" role="group" aria-label="Grid size">
            {['small', 'medium', 'large'].map((size) => (
              <button
                key={size}
                type="button"
                className={gridSize === size ? 'is-active' : ''}
                onClick={() => setGridSize(size)}
                aria-label={`${size} grid`}
                title={`${size[0].toUpperCase()}${size.slice(1)} grid`}
              >
                {size[0].toUpperCase()}
              </button>
            ))}
          </div>
        </div>

      </div>

      {contextMenu && (
        <WorkspaceContextMenu
          menu={contextMenu}
          onClose={() => setContextMenu(null)}
          onOpen={openApp}
          onOpenNewTab={openAppNewTab}
          onToggleFavorite={toggleFavorite}
          onToggleHidden={toggleHidden}
          onMoveTop={moveToTop}
        />
      )}
    </>,
    document.body
  );
};

export default WorkspaceLauncher;
